/**
 * AG Grid Mode Configurations
 *
 * Defines preset configurations for each table type:
 * - COMPSHEET: Sorting, filtering, pinned stats, custom header
 * - FINANCIAL: Read-only, dual-row pattern, collapsible groups, calculation dropdown
 * - INDEX: Checkbox selection, colored percentages, chart integration
 */

import {
  TABLE_MODES,
  getModeHeights,
  getModeDefaultColDef,
  getAlternatingRowStyle,
  getFinancialRowStyle,
  getFinancialRowHeight,
} from './agGridStyles';

/**
 * CompSheet mode configuration
 * Used for: Public Comp Sheet, Market Coverage tables
 * Features: Sorting, filtering, pinned Mean/Median rows, column selection
 */
export const COMPSHEET_CONFIG = {
  mode: TABLE_MODES.COMPSHEET,
  features: {
    sorting: true,
    filtering: true,
    pinnedStats: true,
    columnSelection: true,
    export: true,
  },
  gridProps: {
    pagination: false,
    suppressScrollOnNewData: true,
    domLayout: 'normal',
    enableCellTextSelection: true,
    ensureDomOrder: true,
  },
};

/**
 * Financial mode configuration
 * Used for: Financial Model tables, Key Metrics tables
 * Features: Read-only, value/calculation row pairs, calculation dropdowns
 */
export const FINANCIAL_CONFIG = {
  mode: TABLE_MODES.FINANCIAL,
  features: {
    sorting: false,
    filtering: false,
    pinnedStats: false,
    dualRowPattern: true,
    calculationDropdown: true,
    groupCollapse: false,
    export: true,
  },
  gridProps: {
    pagination: false,
    suppressScrollOnNewData: true,
    domLayout: 'normal',
    enableCellTextSelection: true,
  },
};

/**
 * Financial mode with grouping configuration
 * Used for: Trended Metrics tables with collapsible groups
 */
export const FINANCIAL_GROUPED_CONFIG = {
  mode: TABLE_MODES.FINANCIAL,
  features: {
    sorting: false,
    filtering: false,
    pinnedStats: false,
    dualRowPattern: false,
    calculationDropdown: false,
    groupCollapse: true,
    export: true,
  },
  gridProps: {
    pagination: false,
    suppressScrollOnNewData: true,
    domLayout: 'normal',
    enableCellTextSelection: true,
  },
};

/**
 * Index mode configuration
 * Used for: Index Performance tables
 * Features: Checkbox selection, colored percentages, chart integration
 */
export const INDEX_CONFIG = {
  mode: TABLE_MODES.INDEX,
  features: {
    sorting: true,
    filtering: false,
    pinnedStats: false,
    checkboxSelection: true,
    coloredPercentages: true,
    chartIntegration: true,
    export: false,
  },
  gridProps: {
    pagination: false,
    suppressScrollOnNewData: true,
    domLayout: 'autoHeight',
    enableCellTextSelection: true,
  },
};

/**
 * Get complete configuration for a mode
 * @param {string} mode - Table mode (compsheet, financial, index)
 * @param {Object} theme - MUI theme object
 * @returns {Object} Complete grid configuration
 */
export const getModeConfig = (mode, theme) => {
  const heights = getModeHeights(mode);
  const defaultColDef = getModeDefaultColDef(mode);

  const baseConfig = {
    heights,
    defaultColDef,
  };

  switch (mode) {
    case TABLE_MODES.COMPSHEET:
      return {
        ...baseConfig,
        ...COMPSHEET_CONFIG,
        getRowStyle: getAlternatingRowStyle(theme),
        rowHeight: heights.rowHeight,
        headerHeight: heights.headerHeight,
      };

    case TABLE_MODES.FINANCIAL:
      return {
        ...baseConfig,
        ...FINANCIAL_CONFIG,
        getRowStyle: getFinancialRowStyle(theme),
        getRowHeight: getFinancialRowHeight(heights),
        headerHeight: heights.headerHeight,
      };

    case TABLE_MODES.INDEX:
      return {
        ...baseConfig,
        ...INDEX_CONFIG,
        getRowStyle: getAlternatingRowStyle(theme),
        rowHeight: heights.rowHeight,
        headerHeight: heights.headerHeight,
      };

    default:
      return {
        ...baseConfig,
        ...COMPSHEET_CONFIG,
        getRowStyle: getAlternatingRowStyle(theme),
        rowHeight: heights.rowHeight,
        headerHeight: heights.headerHeight,
      };
  }
};

/**
 * Get specific configuration object
 * @param {string} configName - Name of configuration
 * @returns {Object} Configuration object
 */
export const getNamedConfig = (configName) => {
  const configs = {
    compsheet: COMPSHEET_CONFIG,
    financial: FINANCIAL_CONFIG,
    financialGrouped: FINANCIAL_GROUPED_CONFIG,
    index: INDEX_CONFIG,
  };

  return configs[configName] || COMPSHEET_CONFIG;
};

export default {
  COMPSHEET_CONFIG,
  FINANCIAL_CONFIG,
  FINANCIAL_GROUPED_CONFIG,
  INDEX_CONFIG,
  getModeConfig,
  getNamedConfig,
};
